// Dashboard-app intent cookie. Dropped when a marketing link lands on
// `/signUp?app=<appType>&src=<source>` (AppShell captures it — see the comment there), read by
// useShouldShowOnboarding (to suppress the survey on that load) and consumed ONCE by
// AppIntentLauncher on the first app-shell load (to open that app's add-modal). Both read the
// SAME `hasAppIntent()` signal, so the survey shell and the launcher can never race each other.
//
// ONE cookie, not three: app + source + captured-at are a single JSON payload, so a partial
// write can never produce an intent whose source belongs to an earlier click. Same scope as
// referral-cookies.ts — host-only (no `domain=` — `.optisigns.com` would break localhost),
// non-HttpOnly (JS reads it), Path=/, SameSite=Lax — but a 1-HOUR TTL: the intent is meant to
// survive one signup, not a month of unrelated browsing.

import { safeJsonParse } from '@/lib/safe-format';

const APP_COOKIE = 'os_app_intent';
const ONE_HOUR_SECONDS = 60 * 60;

/** Apps a `?app=` marketing link may target. Hardcoded on purpose (v1): the
 *  `appMetas.dashboard` flag is not exposed to the portal yet, and the prod flag set
 *  includes non-dashboard apps (Trello, Asana, Jira).
 *  Do not add an enterprise-gated app here without a visible fallback — the launcher opens the
 *  modal without the app picker, so the picker's enterprise warning never renders. */
export const DASHBOARD_INTENT_APPS = [
  'microsoft-bi',
  'tableau',
  'google-data-studio',
  'securedDashboard',
  'grafana',
  'databricks',
  'salesforce',
] as const;

export type DashboardIntentApp = (typeof DASHBOARD_INTENT_APPS)[number];

// Short, lowercase, URL-safe attribution token (e.g. `lp`, `blog`, `sales_2026-q3`).
const SOURCE_PATTERN = /^[a-z0-9_-]{1,32}$/;

export function isDashboardIntentApp(app: string | undefined): app is DashboardIntentApp {
  return app !== undefined && DASHBOARD_INTENT_APPS.includes(app as DashboardIntentApp);
}

export function isValidIntentSource(src: string | undefined): src is string {
  return src !== undefined && SOURCE_PATTERN.test(src);
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${ONE_HOUR_SECONDS}; Path=/; SameSite=Lax`;
}

function expireCookie(name: string): void {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export interface AppIntent {
  app: string;
  source?: string;
  /** Unix seconds when the link was clicked. */
  at?: number;
}

/** Cookie payload — `encodeURIComponent(JSON.stringify(StoredIntent))`. Field names are short
 *  because they go over the wire on every request to the host. */
interface StoredIntent {
  app: string;
  src?: string;
  at?: number;
}

/** Caller validates `app`: this writes whatever it is given. */
export function setAppIntent(app: string, source?: string): void {
  const payload: StoredIntent = { app, at: Math.floor(Date.now() / 1000) };
  if (source) payload.src = source;
  writeCookie(APP_COOKIE, JSON.stringify(payload));
}

/** Parsed defensively: anything that is not a JSON object carrying an ALLOWLISTED `app`
 *  (tampered or garbage cookie) is treated as ABSENT, so it can never open an arbitrary app. */
export function getAppIntent(): AppIntent | undefined {
  const raw = readCookie(APP_COOKIE);
  if (raw === undefined) return undefined;
  const parsed = safeJsonParse<unknown>(raw, null);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return undefined;

  const app = 'app' in parsed && typeof parsed.app === 'string' ? parsed.app : undefined;
  if (!isDashboardIntentApp(app)) return undefined;

  const src = 'src' in parsed && typeof parsed.src === 'string' ? parsed.src : undefined;
  const at = 'at' in parsed && typeof parsed.at === 'number' ? parsed.at : undefined;
  return { app, source: isValidIntentSource(src) ? src : undefined, at };
}

export function hasAppIntent(): boolean {
  return getAppIntent() !== undefined;
}

/** One-shot: cleared by AppIntentLauncher after the first app-shell load consumes it. */
export function clearAppIntent(): void {
  expireCookie(APP_COOKIE);
}

/**
 * Capture `?app=<appType>&src=<source>` off a `URLSearchParams` string (`location.search`). An
 * app outside the allowlist writes NOTHING — it does not clear an existing intent either; with
 * a one-hour TTL a stale intent expires on its own. An invalid `src` is dropped, never the
 * intent.
 *
 * The return value is read back from the cookie, so a browser that refused the write reports
 * "not captured" rather than arming a launcher with nothing to consume.
 */
export function captureAppIntentFromSearch(search: string): AppIntent | undefined {
  const params = new URLSearchParams(search);
  const app = params.get('app') ?? undefined;
  if (!isDashboardIntentApp(app)) return undefined;

  const rawSrc = params.get('src') ?? undefined;
  setAppIntent(app, isValidIntentSource(rawSrc) ? rawSrc : undefined);
  return getAppIntent();
}

// ── Consumed-intent session record (FR-14) ────────────────────────────────────
// The cookie is spent the moment the launcher consumes it, but the visitor's screen setup
// happens minutes later, in a different feature. This record survives that gap so
// `screen_paired` can be attributed back to the marketing link that started the session.
//
// `sessionStorage`, not the cookie: it must NOT travel to the server on every request, and it
// must die with the tab — a next-day visit is not the same journey. Written at CONSUMPTION
// (before the pre-hook is answered), so a "Not now" visitor who pairs a screen anyway is still
// attributed to the link they clicked; that is the intent of the measurement.
//
// `lib/logout.ts` clears it with the other per-user session keys (`clearConsumedIntent`), so a
// sign-out in the same tab cannot leave it behind to decorate the NEXT user's `screen_paired`.

const CONSUMED_KEY = 'os_app_intent_consumed';

export interface ConsumedIntentRecord {
  app: string;
  source?: string;
  /** Unix seconds when the intent was consumed. */
  at: number;
}

/** Best-effort: `sessionStorage` throws in a sandboxed iframe (see `lib/app-variant.ts`), and
 *  losing an analytics decoration is never a reason to break the launch. */
export function rememberConsumedIntent(app: string, source?: string): void {
  const record: ConsumedIntentRecord = { app, at: Math.floor(Date.now() / 1000) };
  if (source) record.source = source;
  try {
    sessionStorage.setItem(CONSUMED_KEY, JSON.stringify(record));
  } catch {
    // No storage, no attribution. The launch itself is unaffected.
  }
}

/** Called by `fullLogout`, for the reason in the section note above. Best-effort for the same
 *  reason as the write. */
export function clearConsumedIntent(): void {
  try {
    sessionStorage.removeItem(CONSUMED_KEY);
  } catch {
    // Nothing to clear if there is no storage.
  }
}

/** Parsed defensively — a hand-edited or truncated value reads as ABSENT rather than
 *  decorating an event with garbage. */
export function getConsumedIntent(): ConsumedIntentRecord | undefined {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(CONSUMED_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;

  const parsed = safeJsonParse<unknown>(raw, null);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return undefined;

  const app = 'app' in parsed && typeof parsed.app === 'string' ? parsed.app : undefined;
  const at = 'at' in parsed && typeof parsed.at === 'number' ? parsed.at : undefined;
  if (app === undefined || at === undefined) return undefined;

  const source =
    'source' in parsed && typeof parsed.source === 'string' ? parsed.source : undefined;
  return { app, source, at };
}

/**
 * Register-payload fragment for the dashboard fast path (spec 08 FR-22a). Spread beside
 * `buildOptigrowthRegisterFields()`; the key is OMITTED when there is no cookie so ordinary
 * signups send nothing. The server validates `app` against its own allowlist and stamps
 * `company.payment.appIntentApp/appIntentSource` write-once.
 */
export function buildAppIntentRegisterFields(): { appIntent?: { app: string; source?: string } } {
  const intent = getAppIntent();
  if (!intent) return {};
  return {
    appIntent: intent.source ? { app: intent.app, source: intent.source } : { app: intent.app },
  };
}
